import FormHeader from "@/components/FormHeader";
import InputError from "@/components/InputError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import useSessionStorage from "@/hooks/useSessionStorage";
import { useUser } from "@/hooks/useUser";
import api from "@/lib/axios";
import { DataBerkas, SysBerkas } from "@/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  AlertCircle,
  FileCheck2,
  FileText,
  Loader2,
  UploadCloud,
  X,
} from "lucide-react";
import { useRef, useState } from "react";
import toast from "react-hot-toast";

const postData = async (newData: FormData) => {
  const id_sys_periode = newData.get("id_sys_periode");
  const response = await api.post(
    `/siswa/penerimaan/smbjm/data-berkas/upload?id_sys_periode=${id_sys_periode}`,
    newData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
};

export default function Form({
  method,
  closeModal,
  selectedRow,
  idDataPendaftaran,
}: {
  method: "POST" | "PATCH" | "DELETE";
  closeModal: () => void;
  selectedRow?: SysBerkas | null;
  idDataPendaftaran?: string;
}) {
  const { user } = useUser();
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const [form, setForm] = useState<DataBerkas>({
    id_data_berkas_siswa: "",
    id_data_pendaftaran: idDataPendaftaran || "",
    id_sys_berkas: String(selectedRow?.id_sys_berkas) || "",
    nomor: selectedRow?.data_berkas?.nomor || "",
  });

  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isPost = method === "POST";
  const queryClient = useQueryClient();

  const { mutate, isPending, error } = useMutation({
    mutationFn: isPost ? postData : undefined,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listDataBerkas"] });
      queryClient.invalidateQueries({
        queryKey: ["dataStep", user?.id, sessionPeriode?.id_sys_periode],
      });
      closeModal();
      toast.success(data?.message || "File uploaded successfully.");
    },
    onError: (
      error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  // --- Drag and Drop Handlers ---
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const validateAndSetFile = (uploadedFile: File) => {
    if (selectedRow?.max_upload) {
      const maxSize = Number(selectedRow.max_upload) * 1024; // convert KB to Bytes
      if (uploadedFile.size > maxSize) {
        toast.error(
          `File size is too large. Maximum allowed is ${selectedRow.max_upload} KB`
        );
        return;
      }
    }
    setFile(uploadedFile);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!file) {
      toast.error("Please select a file first.");
      return;
    }

    const formData = new FormData();
    formData.append("id_data_pendaftaran", form.id_data_pendaftaran);
    formData.append("id_sys_berkas", form.id_sys_berkas);
    formData.append("nomor", form.nomor || "");
    formData.append("file_berkas", file);
    if (sessionPeriode?.id_sys_periode)
      formData.append("id_sys_periode", sessionPeriode?.id_sys_periode);

    mutate(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <FormHeader
        icon={<UploadCloud className="text-primary" />}
        title={`Upload ${
          selectedRow?.jenis_berkas?.nama_jenis_berkas || "Document"
        }`}
        description="Please ensure the document is clear, legible, and follows the required format."
      />

      <div className="space-y-4">
        {/* Upload Area */}
        <div className="space-y-2">
          <Label>Document File</Label>

          {!file ? (
            <div
              className={`relative flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-200 ${
                dragActive
                  ? "border-primary bg-primary/5"
                  : "border-slate-300 hover:border-primary hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-full mb-3">
                  <UploadCloud className="w-6 h-6 text-slate-500" />
                </div>
                <p className="mb-1 text-sm text-slate-700 dark:text-slate-300 font-medium">
                  <span className="font-semibold text-primary">
                    Click to upload
                  </span>{" "}
                  or drag & drop
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  PDF, JPG, PNG (Max. {selectedRow?.max_upload || "2048"} KB)
                </p>
              </div>
              <Input
                ref={inputRef}
                id="dropzone-file"
                type="file"
                className="hidden"
                accept=".pdf, image/*"
                onChange={handleChange}
              />
            </div>
          ) : (
            <div className="flex items-center justify-between p-4 border rounded-xl bg-slate-50 dark:bg-slate-900 dark:border-slate-700">
              <div className="flex items-center gap-3 overflow-hidden">
                <div className="p-2 bg-primary/10 text-primary rounded-lg shrink-0">
                  <FileText size={20} />
                </div>
                <div className="flex flex-col min-w-0">
                  <p className="text-sm font-medium truncate max-w-[200px] sm:max-w-xs">
                    {file.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {(file.size / 1024).toFixed(2)} KB
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-slate-400 hover:text-red-500"
                onClick={() => setFile(null)}
              >
                <X size={18} />
              </Button>
            </div>
          )}

          {/* Info Badges */}
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground mt-2">
            {selectedRow?.extension_allowed &&
              Array.isArray(selectedRow.extension_allowed) && (
                <div className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded-md">
                  <AlertCircle size={12} />
                  <span>
                    Allowed Formats: {selectedRow.extension_allowed.join(", ")}
                  </span>
                </div>
              )}
            {selectedRow?.max_upload && (
              <div className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded-md">
                <AlertCircle size={12} />
                <span>Max Size: {selectedRow.max_upload} KB</span>
              </div>
            )}
          </div>
        </div>

        {/* Input Nomor (Conditional) */}
        {selectedRow?.jenis_berkas?.is_add_filed_tambahan ? (
          <div className="space-y-1 pt-2 animate-in fade-in slide-in-from-top-1">
            <Label required>Document Number</Label>
            <Input
              required
              placeholder="Example: 123/SK/2024"
              value={form.nomor}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, nomor: e.target.value }))
              }
            />
            <InputError
              text={error?.response?.data?.errors?.nomor?.join(",")}
            />
          </div>
        ) : null}
      </div>

      {/* Footer Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t mt-4">
        <Button
          type="button"
          variant="ghost"
          onClick={closeModal}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isPending || !file}
          className="min-w-[120px]"
        >
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <FileCheck2 className="w-4 h-4 mr-2" />
              Save
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
